import { BusinessDate } from '../date/business-date';
import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type {
  ActivityId,
  DisciplineId,
  FloorId,
  Id,
  ProjectId,
  ServiceId,
  TowerId,
  UserId,
} from '../shared/ids';
import { activityNaturalKey, type Activity } from '../structure/activity';
import {
  isAvailableForProject as disciplineAvailableForProject,
  type Discipline,
} from '../structure/discipline';
import { floorNaturalKey, type Floor } from '../structure/floor';
import { towerNaturalKey, type Tower } from '../structure/tower';
import {
  isAvailableForProject as serviceAvailableForProject,
  serviceNaturalKey,
  type Service,
} from '../takeoff/service';
import { roundQuantity } from './unit-of-measure';

/**
 * O item do cronograma (Fase 25, ADR-160).
 *
 * É **um serviço numa frente**, com um trecho opcional:
 *
 * ```text
 * frente (pavimento × disciplina, RN-004) × serviço (nulo = frente inteira) × trecho
 * ```
 *
 * O cronograma do GanttPRO desce até o serviço — "Contrapiso / 3º Pavimento" —, e nenhuma
 * peça do sistema guardava isso: a `Activity` tem um par de datas por frente, e a linha de
 * material (`ScheduleEntry`) exige material e data de necessidade. Serviço nulo é a barra
 * que não desce ("Estrutura / 3º Pavimento" vira a janela de PILARES, VIGAS, LAJE e
 * ESCADAS, D1); o trecho é a face da fachada e o muro (D2).
 *
 * Tudo aqui é puro: o classificador da importação recebe o cadastro já lido e devolve o
 * que a confirmação faria, sem tocar banco nenhum (RN-030).
 */
export type ScheduleItemId = Id<'ScheduleItem'>;

/**
 * De onde vêm a quantidade e o HH do item (Fase 31, RN-418).
 *
 * - `ARQUIVO`: do GanttPRO ou do modelo de planilha — como todo item nascia até a Fase 31;
 * - `QUANTITATIVO`: do quantitativo da obra, pavimento × serviço. Ele **vence** o arquivo: o
 *   número que o arquivo trouxer depois vai para `fileQuantity` e `fileLaborHours`, para a
 *   tela comparar, e não substitui o do quantitativo (D2).
 */
export type QuantitySource = 'ARQUIVO' | 'QUANTITATIVO';

export interface ScheduleItem {
  readonly id: ScheduleItemId;
  readonly projectId: ProjectId;
  /** A frente — pavimento × disciplina. Identidade: não muda depois de criado. */
  readonly activityId: ActivityId;
  /** Ausente: a frente inteira (RN-320). */
  readonly serviceId?: ServiceId;
  /** O trecho da frente — FRENTE, FUNDOS, MUROS. Ausente: a frente toda. */
  readonly segment?: string;
  /** EDT do arquivo. Não é única: uma tarefa pode virar vários itens (D1, D2). */
  readonly wbsCode?: string;
  /** O nome da tarefa como veio no arquivo — só para mostrar a origem. */
  readonly sourceName?: string;
  readonly plannedStart: string;
  readonly plannedEnd: string;
  /** Linha de base: nasce igual ao previsto e nunca é reescrita (RN-322, ADR-162). */
  readonly baselineStart: string;
  readonly baselineEnd: string;
  /** EDT das antecessoras, término-início, resolvidas na leitura (RN-323). */
  readonly predecessorWbsCodes: readonly string[];
  readonly quantity?: number;
  /** A unidade da quantidade, como o arquivo a escreveu. */
  readonly unit?: string;
  /** Quantidade estimada — rateio por pavimento, levantamento incompleto (RN-324, RN-326). */
  readonly quantityProvisional: boolean;
  /** Horas-homem do item. */
  readonly laborHours?: number;
  /** 0 a 100, lançado à mão até a fatia que soma a medição. */
  readonly progressPct: number;
  /** De onde vêm a quantidade e o HH (Fase 31, RN-418). O item do arquivo é `ARQUIVO`. */
  readonly quantitySource: QuantitySource;
  /**
   * O número do arquivo, guardado quando o quantitativo assumiu o item (RN-418, D2).
   *
   * A primeira vez que o quantitativo assume, a quantidade e o HH que o item tinha — os do
   * arquivo — vêm para cá; a reimportação seguinte do arquivo também escreve aqui. Ausente:
   * o arquivo não trouxe o número, ou o item nasceu do quantitativo e o arquivo nunca falou dele.
   */
  readonly fileQuantity?: number;
  readonly fileLaborHours?: number;
  /**
   * A quantidade e o HH que o quantitativo tinha quando gerou ou atualizou o item (RN-419).
   *
   * É contra estes, e não contra `quantity`, que o quantitativo de hoje é comparado: são o
   * retrato do que foi aplicado, e a diferença é o que a faixa "o quantitativo mudou" conta.
   */
  readonly takeoffQuantity?: number;
  readonly takeoffLaborHours?: number;
  /** A equipe (pessoas) com que a geração calculou a duração (D5). */
  readonly crewSize?: number;
  /**
   * Quando o quantitativo deixou de ter este pavimento × serviço (RN-419).
   *
   * O item **não** é apagado — ele pode ter avanço, medição e material que o segue —, só
   * marcado. Volta a ficar sem a marca quando o quantitativo volta a tê-lo.
   */
  readonly takeoffMissingAt?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly updatedByUserId?: UserId;
  readonly deletedAt?: string;
}

// ── Erros ────────────────────────────────────────────────────────────────────

export const SCHEDULE_ITEM_ERROR = {
  NOT_FOUND: 'SCHEDULE_ITEM.NOT_FOUND',
  DATE_INVALID: 'SCHEDULE_ITEM.DATE_INVALID',
  PERIOD_INVALID: 'SCHEDULE_ITEM.PERIOD_INVALID',
  NEGATIVE_NUMBER: 'SCHEDULE_ITEM.NEGATIVE_NUMBER',
  PROGRESS_OUT_OF_RANGE: 'SCHEDULE_ITEM.PROGRESS_OUT_OF_RANGE',
  // Os da conferência da importação: cada um diz o que falta e como resolver.
  REQUIRED: 'SCHEDULE_ITEM.REQUIRED',
  TOWER_NOT_FOUND: 'SCHEDULE_ITEM.TOWER_NOT_FOUND',
  FLOOR_MISSING: 'SCHEDULE_ITEM.FLOOR_MISSING',
  FLOOR_LEVEL_MISSING: 'SCHEDULE_ITEM.FLOOR_LEVEL_MISSING',
  FLOOR_LEVEL_INVALID: 'SCHEDULE_ITEM.FLOOR_LEVEL_INVALID',
  DISCIPLINE_MISSING: 'SCHEDULE_ITEM.DISCIPLINE_MISSING',
  ACTIVITY_MISSING: 'SCHEDULE_ITEM.ACTIVITY_MISSING',
  SERVICE_MISSING: 'SCHEDULE_ITEM.SERVICE_MISSING',
  DUPLICATE_IN_FILE: 'SCHEDULE_ITEM.DUPLICATE_IN_FILE',
} as const;

export const SCHEDULE_ITEM_ERROR_MESSAGE: Readonly<Record<string, string>> = {
  [SCHEDULE_ITEM_ERROR.NOT_FOUND]: 'Item do cronograma não encontrado.',
  [SCHEDULE_ITEM_ERROR.DATE_INVALID]: 'Data inválida. Use o formato AAAA-MM-DD.',
  [SCHEDULE_ITEM_ERROR.PERIOD_INVALID]: 'O início não pode ser depois do fim.',
  [SCHEDULE_ITEM_ERROR.NEGATIVE_NUMBER]: 'Quantidade e horas-homem não podem ser negativas.',
  [SCHEDULE_ITEM_ERROR.PROGRESS_OUT_OF_RANGE]: 'O avanço vai de 0 a 100.',
};

function falha(code: string): DomainError {
  return domainError(code, SCHEDULE_ITEM_ERROR_MESSAGE[code]!);
}

// ── Os números do item ───────────────────────────────────────────────────────

/**
 * Casas guardadas da quantidade e do HH — as do `numeric(14, 6)` do servidor.
 *
 * O rateio por pavimento (D2) divide por 14 e devolve dízima: guardar o número cheio no
 * navegador e cortado no servidor faria a reimportação do mesmo arquivo achar diferença
 * onde não há, e todo item viraria "atualizado" para sempre.
 */
export const SCHEDULE_ITEM_DECIMALS = 6;

/** O avanço guarda duas casas — o `numeric(5, 2)` do servidor. */
const PROGRESS_DECIMALS = 2;

function arredondar(value: number | undefined, decimals = SCHEDULE_ITEM_DECIMALS) {
  return value === undefined ? undefined : roundQuantity(value, decimals);
}

function textoOuAusente(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * RN-323 — a predecessora é EDT de **outra** tarefa.
 *
 * A própria EDT sai (o GanttPRO não a escreve, mas o modelo digitado pode), a repetida
 * sai, e a ordem do arquivo fica: é a ordem em que quem planejou as leu.
 */
export function normalizePredecessors(
  codes: readonly string[],
  ownWbsCode: string | undefined,
): string[] {
  const vistos = new Set<string>();
  const resultado: string[] = [];
  for (const code of codes) {
    const limpo = code.trim();
    if (limpo === '' || limpo === ownWbsCode || vistos.has(limpo)) continue;
    vistos.add(limpo);
    resultado.push(limpo);
  }
  return resultado;
}

function dataValida(value: string): boolean {
  return BusinessDate.parse(value).ok;
}

function numeroValido(value: number | undefined): boolean {
  return value === undefined || (Number.isFinite(value) && value >= 0);
}

/**
 * O que uma linha do arquivo diz sobre o item — já limpo, arredondado e sem a própria
 * EDT nas predecessoras.
 *
 * Ausente é "a linha não traz": a quantidade nula **mantém** a do item na reimportação
 * (RN-328), e o mesmo vale para o HH, a EDT e o nome de origem — o arquivo que não fala de
 * uma coisa não a apaga.
 */
export interface ScheduleItemValues {
  readonly wbsCode?: string;
  readonly sourceName?: string;
  readonly plannedStart: string;
  readonly plannedEnd: string;
  readonly predecessorWbsCodes: readonly string[];
  readonly quantity?: number;
  readonly unit?: string;
  readonly quantityProvisional: boolean;
  readonly laborHours?: number;
}

/** Confere os valores: datas reais (RN-321) e números não negativos (RN-324). */
function conferirValores(values: ScheduleItemValues): DomainError | undefined {
  if (!dataValida(values.plannedStart) || !dataValida(values.plannedEnd)) {
    return falha(SCHEDULE_ITEM_ERROR.DATE_INVALID);
  }
  if (values.plannedStart > values.plannedEnd) return falha(SCHEDULE_ITEM_ERROR.PERIOD_INVALID);
  if (!numeroValido(values.quantity) || !numeroValido(values.laborHours)) {
    return falha(SCHEDULE_ITEM_ERROR.NEGATIVE_NUMBER);
  }
  return undefined;
}

// ── Criação ──────────────────────────────────────────────────────────────────

export interface CreateScheduleItemInput {
  readonly id: ScheduleItemId;
  readonly projectId: ProjectId;
  readonly activityId: ActivityId;
  readonly serviceId?: ServiceId;
  readonly segment?: string;
  readonly values: ScheduleItemValues;
  readonly now: string;
  readonly userId?: UserId;
}

/**
 * Cria o item, com a linha de base igual ao previsto (RN-322).
 *
 * A marca de provisória só fica com quantidade: provisória sem número é selo sobre um
 * travessão, e o banco também a recusa.
 */
export function createScheduleItem(
  input: CreateScheduleItemInput,
): Result<ScheduleItem, DomainError> {
  const problema = conferirValores(input.values);
  if (problema !== undefined) return err(problema);

  const { values } = input;
  const segment = textoOuAusente(input.segment);
  const wbsCode = textoOuAusente(values.wbsCode);
  const sourceName = textoOuAusente(values.sourceName);
  const quantity = arredondar(values.quantity);
  const unit = quantity === undefined ? undefined : textoOuAusente(values.unit);
  const laborHours = arredondar(values.laborHours);

  return ok({
    id: input.id,
    projectId: input.projectId,
    activityId: input.activityId,
    plannedStart: values.plannedStart,
    plannedEnd: values.plannedEnd,
    baselineStart: values.plannedStart,
    baselineEnd: values.plannedEnd,
    predecessorWbsCodes: normalizePredecessors(values.predecessorWbsCodes, wbsCode),
    quantityProvisional: quantity !== undefined && values.quantityProvisional,
    progressPct: 0,
    quantitySource: 'ARQUIVO',
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.serviceId === undefined ? {} : { serviceId: input.serviceId }),
    ...(segment === undefined ? {} : { segment }),
    ...(wbsCode === undefined ? {} : { wbsCode }),
    ...(sourceName === undefined ? {} : { sourceName }),
    ...(quantity === undefined ? {} : { quantity }),
    ...(unit === undefined ? {} : { unit }),
    ...(laborHours === undefined ? {} : { laborHours }),
    ...(input.userId === undefined ? {} : { updatedByUserId: input.userId }),
  });
}

/**
 * Chave natural do item (RN-320): frente, serviço e trecho normalizado.
 *
 * O trecho passa pela normalização do domínio (RN-036) — a mesma do índice único do
 * servidor —, porque ele chega de arquivo: "FRENTE" e "Frente " são a mesma face.
 */
export function scheduleItemNaturalKey(
  activityId: ActivityId,
  serviceId: ServiceId | undefined,
  segment: string | undefined,
): string {
  return `${activityId}|${serviceId ?? ''}|${normalizeName(segment ?? '')}`;
}

// ── Reimportação ─────────────────────────────────────────────────────────────

export interface ScheduleItemChange {
  readonly item: ScheduleItem;
  readonly changed: boolean;
}

function mesmaLista(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** Mudou alguma coisa que o arquivo ou a tela escrevem? A linha de base nunca entra. */
function diferem(antes: ScheduleItem, depois: ScheduleItem): boolean {
  return (
    antes.plannedStart !== depois.plannedStart ||
    antes.plannedEnd !== depois.plannedEnd ||
    antes.wbsCode !== depois.wbsCode ||
    antes.sourceName !== depois.sourceName ||
    !mesmaLista(antes.predecessorWbsCodes, depois.predecessorWbsCodes) ||
    antes.quantity !== depois.quantity ||
    antes.unit !== depois.unit ||
    antes.quantityProvisional !== depois.quantityProvisional ||
    antes.laborHours !== depois.laborHours ||
    antes.progressPct !== depois.progressPct ||
    antes.fileQuantity !== depois.fileQuantity ||
    antes.fileLaborHours !== depois.fileLaborHours
  );
}

/**
 * Aplica a linha reimportada sobre o item (RN-322, RN-328).
 *
 * - o **previsto** passa a ser o do arquivo; a **linha de base** fica (RN-322);
 * - a quantidade nula **mantém** a do item, com a unidade e a marca dela (RN-328); com
 *   número, os três vêm do arquivo juntos — a unidade é a do número;
 * - o HH, a EDT e o nome de origem seguem a mesma regra: o arquivo que não os traz não
 *   os apaga;
 * - as predecessoras são da EDT: a linha que traz EDT traz a lista inteira, e a vazia é
 *   "sem antecessora" (a Rev.01 pode ter tirado uma); a linha sem EDT não fala de
 *   dependência, e a lista do item fica;
 * - o avanço é da obra, e não do arquivo: a reimportação nunca o toca;
 * - no item que o quantitativo assumiu (`QUANTITATIVO`, RN-418), a quantidade, a unidade e o
 *   HH continuam os do quantitativo: o número do arquivo vai para `fileQuantity` e
 *   `fileLaborHours`, para a tela comparar (D2). As datas seguem a regra de sempre.
 *
 * Nada mudou → o mesmo item, com `changed: false` — reimportar o mesmo arquivo é
 * idempotente, e não reescreve `updatedAt` de ninguém (RN-032).
 */
export function reimportScheduleItem(
  item: ScheduleItem,
  values: ScheduleItemValues,
  now: string,
  userId?: UserId,
): Result<ScheduleItemChange, DomainError> {
  const problema = conferirValores(values);
  if (problema !== undefined) return err(problema);

  const wbsCode = textoOuAusente(values.wbsCode) ?? item.wbsCode;
  const sourceName = textoOuAusente(values.sourceName) ?? item.sourceName;
  const predecessorWbsCodes =
    textoOuAusente(values.wbsCode) === undefined
      ? item.predecessorWbsCodes
      : normalizePredecessors(values.predecessorWbsCodes, wbsCode);

  const quantidadeDoArquivo = arredondar(values.quantity);
  const hhDoArquivo = arredondar(values.laborHours);
  const doQuantitativo = item.quantitySource === 'QUANTITATIVO';

  // RN-418 — o quantitativo vence: o arquivo só atualiza o número guardado para comparar.
  const quantity = doQuantitativo ? item.quantity : (quantidadeDoArquivo ?? item.quantity);
  const unit =
    doQuantitativo || quantidadeDoArquivo === undefined ? item.unit : textoOuAusente(values.unit);
  const quantityProvisional =
    doQuantitativo || quantidadeDoArquivo === undefined
      ? item.quantityProvisional
      : values.quantityProvisional;
  const laborHours = doQuantitativo ? item.laborHours : (hhDoArquivo ?? item.laborHours);
  const fileQuantity = doQuantitativo
    ? (quantidadeDoArquivo ?? item.fileQuantity)
    : item.fileQuantity;
  const fileLaborHours = doQuantitativo
    ? (hhDoArquivo ?? item.fileLaborHours)
    : item.fileLaborHours;

  const {
    wbsCode: _wbs,
    sourceName: _origem,
    quantity: _quantidade,
    unit: _unidade,
    laborHours: _hh,
    fileQuantity: _doArquivo,
    fileLaborHours: _hhDoArquivo,
    updatedByUserId: _quem,
    ...fixo
  } = item;

  const candidato: ScheduleItem = {
    ...fixo,
    plannedStart: values.plannedStart,
    plannedEnd: values.plannedEnd,
    predecessorWbsCodes,
    quantityProvisional: quantity !== undefined && quantityProvisional,
    ...(wbsCode === undefined ? {} : { wbsCode }),
    ...(sourceName === undefined ? {} : { sourceName }),
    ...(quantity === undefined ? {} : { quantity }),
    ...(unit === undefined ? {} : { unit }),
    ...(laborHours === undefined ? {} : { laborHours }),
    ...(fileQuantity === undefined ? {} : { fileQuantity }),
    ...(fileLaborHours === undefined ? {} : { fileLaborHours }),
  };

  if (!diferem(item, candidato)) return ok({ item, changed: false });

  return ok({
    item: {
      ...candidato,
      updatedAt: now,
      ...(userId === undefined
        ? item.updatedByUserId === undefined
          ? {}
          : { updatedByUserId: item.updatedByUserId }
        : { updatedByUserId: userId }),
    },
    changed: true,
  });
}

// ── Edição ───────────────────────────────────────────────────────────────────

export interface RescheduleScheduleItemInput {
  readonly plannedStart: string;
  readonly plannedEnd: string;
  readonly progressPct: number;
}

/**
 * Reprograma o item e lança o avanço (RN-321, RN-322).
 *
 * Só o previsto e o avanço: a linha de base é a promessa original, e quem a mudasse pela
 * tela apagaria justamente a diferença que ela existe para mostrar.
 */
export function rescheduleScheduleItem(
  item: ScheduleItem,
  input: RescheduleScheduleItemInput,
  now: string,
  userId?: UserId,
): Result<ScheduleItemChange, DomainError> {
  if (!dataValida(input.plannedStart) || !dataValida(input.plannedEnd)) {
    return err(falha(SCHEDULE_ITEM_ERROR.DATE_INVALID));
  }
  if (input.plannedStart > input.plannedEnd) {
    return err(falha(SCHEDULE_ITEM_ERROR.PERIOD_INVALID));
  }
  if (!Number.isFinite(input.progressPct) || input.progressPct < 0 || input.progressPct > 100) {
    return err(falha(SCHEDULE_ITEM_ERROR.PROGRESS_OUT_OF_RANGE));
  }

  const candidato: ScheduleItem = {
    ...item,
    plannedStart: input.plannedStart,
    plannedEnd: input.plannedEnd,
    progressPct: roundQuantity(input.progressPct, PROGRESS_DECIMALS),
  };
  if (!diferem(item, candidato)) return ok({ item, changed: false });

  return ok({
    item: {
      ...candidato,
      updatedAt: now,
      ...(userId === undefined ? {} : { updatedByUserId: userId }),
    },
    changed: true,
  });
}

// ── Ordem ────────────────────────────────────────────────────────────────────

/**
 * Ordem da EDT: "1.4.10" depois de "1.4.9", e não antes — a comparação é por número, nível
 * a nível. Sem EDT vai para o fim.
 */
export function compareWbsCodes(a: string | undefined, b: string | undefined): number {
  if (a === b) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  const pa = a.split('.');
  const pb = b.split('.');
  for (let index = 0; index < Math.max(pa.length, pb.length); index += 1) {
    const xa = pa[index];
    const xb = pb[index];
    if (xa === undefined) return -1;
    if (xb === undefined) return 1;
    const na = Number(xa);
    const nb = Number(xb);
    const porNumero =
      Number.isFinite(na) && Number.isFinite(nb) ? na - nb : xa.localeCompare(xb, 'pt-BR');
    if (porNumero !== 0) return porNumero;
  }
  return 0;
}

// ── A janela que as linhas de material seguem (Fase 26) ──────────────────────

/**
 * O par (frente, serviço) que liga a linha de material ao item (ADR-166).
 *
 * Sem o trecho: a fachada tem até quatro itens para o mesmo serviço — uma face cada —, e a
 * linha de material é do serviço na frente, não de uma face. O serviço nulo é a frente
 * inteira, e casa com a linha sem serviço (RN-342).
 */
export function itemFrontServiceKey(activityId: ActivityId, serviceId: ServiceId | undefined) {
  return `${activityId}|${serviceId ?? '-'}`;
}

/** As datas que os itens vivos de um (frente, serviço) cobrem, juntos. */
export interface ItemWindow {
  readonly plannedStart: string;
  readonly plannedEnd: string;
}

/**
 * RN-342 — a janela de cada (frente, serviço): o menor início e o maior fim dos itens
 * vivos.
 *
 * O menor início, e não o do primeiro trecho do arquivo: o material da fachada precisa
 * estar na obra quando a **primeira** face começa. O maior fim fecha a janela — é até ali
 * que o serviço consome.
 */
export function itemWindowsByFrontService(
  items: readonly ScheduleItem[],
): ReadonlyMap<string, ItemWindow> {
  const windows = new Map<string, ItemWindow>();
  for (const item of items) {
    if (item.deletedAt !== undefined) continue;
    const key = itemFrontServiceKey(item.activityId, item.serviceId);
    const atual = windows.get(key);
    windows.set(key, {
      plannedStart:
        atual === undefined || item.plannedStart < atual.plannedStart
          ? item.plannedStart
          : atual.plannedStart,
      plannedEnd:
        atual === undefined || item.plannedEnd > atual.plannedEnd
          ? item.plannedEnd
          : atual.plannedEnd,
    });
  }
  return windows;
}

/**
 * RN-342, RN-344 — a data de necessidade é o início menos a margem de chegada da obra, em
 * dias **corridos**: o caminhão chega no sábado, e a conta do material não pula o fim de
 * semana como a do serviço pularia.
 */
export function requiredDateBeforeStart(plannedStart: string, marginDays: number): string {
  const margem = Number.isFinite(marginDays) && marginDays > 0 ? Math.trunc(marginDays) : 0;
  return BusinessDate.of(plannedStart).subtractDays(margem).toString();
}

/** A janela que a linha de material recebe: as datas do serviço e a necessidade (RN-342). */
export function entryWindowFor(
  window: ItemWindow,
  marginDays: number,
): { readonly plannedStart: string; readonly plannedEnd: string; readonly requiredDate: string } {
  return {
    plannedStart: window.plannedStart,
    plannedEnd: window.plannedEnd,
    requiredDate: requiredDateBeforeStart(window.plannedStart, marginDays),
  };
}

// ── A importação ─────────────────────────────────────────────────────────────

/**
 * A linha de item como o leitor a produz (ADR-161).
 *
 * É o espelho, no domínio, de `ScheduleItemRow` (`contracts/schedule-items.ts`): o domínio
 * não conhece o contrato, e o caso de uso passa as linhas validadas direto para cá — se os
 * dois divergirem, é o compilador que reclama ali.
 */
export interface ScheduleItemImportRow {
  readonly rowNumber: number;
  readonly towerName: string;
  readonly floorName: string;
  /** Nível para criar o pavimento que faltar. Nulo: o pavimento precisa existir. */
  readonly floorLevel: number | null;
  readonly disciplineName: string;
  readonly serviceName: string | null;
  readonly segment: string | null;
  readonly wbsCode: string | null;
  readonly sourceName: string | null;
  readonly plannedStart: string;
  readonly plannedEnd: string;
  readonly predecessorWbsCodes: readonly string[];
  readonly quantity: number | null;
  readonly unit: string | null;
  readonly quantityProvisional: boolean;
  readonly laborHours: number | null;
}

/** RN-035: criar o que falta é escolha explícita, nunca padrão. */
export interface ScheduleItemImportOptions {
  readonly createMissingFloors: boolean;
  readonly createMissingActivities: boolean;
  readonly createMissingDisciplines: boolean;
  readonly createMissingServices: boolean;
}

export type ScheduleItemRowStatus = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'ERROR';

export interface ScheduleItemImportIssue {
  readonly code: string;
  readonly message: string;
}

export interface ScheduleItemImportCreates {
  readonly floor: boolean;
  readonly activity: boolean;
  readonly discipline: boolean;
  readonly service: boolean;
}

/**
 * O que a confirmação faria com uma linha — e o que ela já resolveu no cadastro.
 *
 * Os ids presentes são do que **existe**; o ausente, com a marca em `creates`, é o que a
 * confirmação cria. `creates` vale para cada linha que depende da criação: dez linhas do
 * mesmo pavimento novo dizem as dez que ele nasce, e a conta do que nasce, sem repetição,
 * está em `missing`.
 */
export interface ScheduleItemRowPlan {
  readonly rowNumber: number;
  readonly status: ScheduleItemRowStatus;
  readonly issues: readonly ScheduleItemImportIssue[];
  readonly creates: ScheduleItemImportCreates;
  readonly row: ScheduleItemImportRow;
  readonly values: ScheduleItemValues;
  readonly towerId?: TowerId;
  readonly floorId?: FloorId;
  readonly disciplineId?: DisciplineId;
  readonly activityId?: ActivityId;
  readonly serviceId?: ServiceId;
  /** O item que a linha atualiza (UPDATE) ou repete (UNCHANGED). */
  readonly existing?: ScheduleItem;
}

export interface ScheduleItemsImportPlan {
  readonly summary: {
    readonly total: number;
    readonly create: number;
    readonly update: number;
    readonly unchanged: number;
    readonly error: number;
  };
  readonly rows: readonly ScheduleItemRowPlan[];
  /** O que falta no cadastro, sem repetição — a conferência mostra antes das opções. */
  readonly missing: {
    readonly floors: readonly {
      readonly towerName: string;
      readonly floorName: string;
      readonly level: number | null;
    }[];
    readonly disciplines: readonly string[];
    readonly services: readonly { readonly disciplineName: string; readonly serviceName: string }[];
    readonly activities: number;
  };
}

export interface ClassifyScheduleItemsImportInput {
  readonly projectId: ProjectId;
  readonly rows: readonly ScheduleItemImportRow[];
  readonly options: ScheduleItemImportOptions;
  readonly towers: readonly Tower[];
  readonly floors: readonly Floor[];
  /** As disponíveis para a obra — globais e dela (RN-007). */
  readonly disciplines: readonly Discipline[];
  readonly activities: readonly Activity[];
  /** Os disponíveis para a obra — globais e dela (RN-007). */
  readonly services: readonly Service[];
  /** Os itens vivos da obra. */
  readonly items: readonly ScheduleItem[];
}

/** Os valores do item que a linha descreve, limpos (RN-323) e arredondados. */
export function scheduleItemValuesFromRow(row: ScheduleItemImportRow): ScheduleItemValues {
  const wbsCode = textoOuAusente(row.wbsCode);
  const sourceName = textoOuAusente(row.sourceName);
  const quantity = arredondar(row.quantity ?? undefined);
  const unit = textoOuAusente(row.unit);
  const laborHours = arredondar(row.laborHours ?? undefined);
  return {
    plannedStart: row.plannedStart,
    plannedEnd: row.plannedEnd,
    predecessorWbsCodes: normalizePredecessors(row.predecessorWbsCodes, wbsCode),
    quantityProvisional: quantity !== undefined && row.quantityProvisional,
    ...(wbsCode === undefined ? {} : { wbsCode }),
    ...(sourceName === undefined ? {} : { sourceName }),
    ...(quantity === undefined ? {} : { quantity }),
    ...(unit === undefined ? {} : { unit }),
    ...(laborHours === undefined ? {} : { laborHours }),
  };
}

const NADA_CRIADO: ScheduleItemImportCreates = {
  floor: false,
  activity: false,
  discipline: false,
  service: false,
};

/**
 * Classifica cada linha da importação (RN-320 a RN-325, RN-328).
 *
 * A resolução é **por nome normalizado** (RN-036): torre na obra, pavimento na torre,
 * disciplina entre as que a obra alcança, serviço dentro da disciplina — as mesmas
 * chaves naturais dos índices únicos, para que o que a conferência acha seja o que o
 * banco acharia.
 *
 * - torre que não existe → `ERROR`: o cronograma é da Torre 2, e criar torre por arquivo
 *   não é opção desta importação;
 * - pavimento, disciplina, frente ou serviço que faltam → `ERROR` com o nome escrito, a
 *   menos que a opção de criar esteja marcada (RN-035); o pavimento precisa ainda do
 *   nível na linha, porque é ele que o ordena (RN-005);
 * - datas fora do calendário ou início depois do fim → `ERROR` (RN-321); número negativo
 *   → `ERROR` (RN-324);
 * - a mesma chave (frente, serviço, trecho) repetida **no arquivo** → `ERROR` na segunda
 *   (RN-320, RN-037): duas linhas para um item é engano, e somá-las inventaria datas;
 * - item que já existe → `UPDATE` ou `UNCHANGED`, pelo que a reimportação mudaria
 *   (RN-322, RN-328);
 * - o resto → `CREATE`.
 *
 * O item que o arquivo não cita **fica**: a importação é por item, e o arquivo da Torre 2
 * não tem autoridade sobre os itens das outras torres.
 */
export function classifyScheduleItemsImport(
  input: ClassifyScheduleItemsImportInput,
): ScheduleItemsImportPlan {
  const { projectId, options } = input;

  const towerByKey = new Map(
    input.towers.map((tower) => [towerNaturalKey(projectId, tower.name), tower]),
  );
  const floorByKey = new Map(
    input.floors.map((floor) => [floorNaturalKey(floor.towerId, floor.name), floor]),
  );
  const disciplineByKey = new Map<string, Discipline>();
  // A da obra vence a global de mesmo nome: é a que alguém criou de propósito para esta
  // obra. Na prática o índice do cadastro impede o par, e isto é só a ordem certa.
  for (const discipline of input.disciplines) {
    if (!disciplineAvailableForProject(discipline, projectId)) continue;
    const key = normalizeName(discipline.name);
    const atual = disciplineByKey.get(key);
    if (atual === undefined || atual.projectId === undefined) disciplineByKey.set(key, discipline);
  }
  const activityByKey = new Map(
    input.activities.map((activity) => [
      activityNaturalKey(activity.floorId, activity.disciplineId),
      activity,
    ]),
  );
  const serviceByKey = new Map(
    input.services
      .filter((service) => serviceAvailableForProject(service, projectId))
      .map((service) => [serviceNaturalKey(service.disciplineId, service.name), service]),
  );
  const itemByKey = new Map(
    input.items
      .filter((item) => item.deletedAt === undefined)
      .map((item) => [scheduleItemNaturalKey(item.activityId, item.serviceId, item.segment), item]),
  );

  const missingFloors = new Map<
    string,
    { towerName: string; floorName: string; level: number | null }
  >();
  const missingDisciplines = new Map<string, string>();
  const missingServices = new Map<string, { disciplineName: string; serviceName: string }>();
  const missingActivities = new Set<string>();
  const seen = new Set<string>();

  const rows: ScheduleItemRowPlan[] = input.rows.map((row) => {
    const issues: ScheduleItemImportIssue[] = [];
    const creates = { floor: false, activity: false, discipline: false, service: false };
    const values = scheduleItemValuesFromRow(row);

    const towerName = row.towerName.trim();
    const floorName = row.floorName.trim();
    const disciplineName = row.disciplineName.trim();
    const serviceName = textoOuAusente(row.serviceName);
    const segment = textoOuAusente(row.segment);

    if (towerName === '' || floorName === '' || disciplineName === '') {
      issues.push({
        code: SCHEDULE_ITEM_ERROR.REQUIRED,
        message: 'Torre, pavimento e disciplina são obrigatórios.',
      });
    }

    // ── Torre ────────────────────────────────────────────────────────────────
    const towerKey = towerNaturalKey(projectId, towerName);
    const tower = towerName === '' ? undefined : towerByKey.get(towerKey);
    if (towerName !== '' && tower === undefined) {
      issues.push({
        code: SCHEDULE_ITEM_ERROR.TOWER_NOT_FOUND,
        message: `A torre "${towerName}" não existe nesta obra. Cadastre-a na Estrutura antes de importar.`,
      });
    }

    // ── Pavimento ────────────────────────────────────────────────────────────
    const floor =
      tower === undefined || floorName === ''
        ? undefined
        : floorByKey.get(floorNaturalKey(tower.id, floorName));
    // Sem torre, o pavimento não tem onde nascer: a falta dele não é listada — a da torre
    // já é o erro da linha.
    const floorFalta = tower !== undefined && floorName !== '' && floor === undefined;
    if (floorFalta) {
      const key = floorNaturalKey(tower.id, floorName);
      const anterior = missingFloors.get(key);
      if (anterior === undefined || anterior.level === null) {
        missingFloors.set(key, { towerName: tower.name, floorName, level: row.floorLevel });
      }
      if (row.floorLevel === null) {
        issues.push({
          code: SCHEDULE_ITEM_ERROR.FLOOR_LEVEL_MISSING,
          message: `O pavimento "${floorName}" não existe na ${tower.name}, e a linha não traz o nível para criá-lo.`,
        });
      } else if (!Number.isInteger(row.floorLevel)) {
        // Ordenar por nível fracionário poria o pavimento entre dois andares (RN-005).
        issues.push({
          code: SCHEDULE_ITEM_ERROR.FLOOR_LEVEL_INVALID,
          message: `O nível ${String(row.floorLevel)} do pavimento "${floorName}" não é um número inteiro.`,
        });
      } else if (!options.createMissingFloors) {
        issues.push({
          code: SCHEDULE_ITEM_ERROR.FLOOR_MISSING,
          message: `O pavimento "${floorName}" não existe na ${tower.name}. Marque para criar os pavimentos que faltam.`,
        });
      } else {
        creates.floor = true;
      }
    }

    // ── Disciplina ───────────────────────────────────────────────────────────
    const disciplineKey = normalizeName(disciplineName);
    const discipline = disciplineName === '' ? undefined : disciplineByKey.get(disciplineKey);
    if (disciplineName !== '' && discipline === undefined) {
      if (!missingDisciplines.has(disciplineKey))
        missingDisciplines.set(disciplineKey, disciplineName);
      if (!options.createMissingDisciplines) {
        issues.push({
          code: SCHEDULE_ITEM_ERROR.DISCIPLINE_MISSING,
          message: `A disciplina "${disciplineName}" não existe. Marque para criar as que faltam no catálogo da obra.`,
        });
      } else {
        creates.discipline = true;
      }
    }

    // ── Frente ───────────────────────────────────────────────────────────────
    const activity =
      floor === undefined || discipline === undefined
        ? undefined
        : activityByKey.get(activityNaturalKey(floor.id, discipline.id));
    const activityFalta =
      tower !== undefined && floorName !== '' && disciplineName !== '' && activity === undefined;
    if (activityFalta) {
      missingActivities.add(`${towerKey}|${normalizeName(floorName)}|${disciplineKey}`);
      if (!options.createMissingActivities) {
        issues.push({
          code: SCHEDULE_ITEM_ERROR.ACTIVITY_MISSING,
          message: `A frente ${disciplineName} · ${floorName} não existe. Marque para criar as frentes que faltam.`,
        });
      } else {
        creates.activity = true;
      }
    }

    // ── Serviço ──────────────────────────────────────────────────────────────
    const service =
      serviceName === undefined || discipline === undefined
        ? undefined
        : serviceByKey.get(serviceNaturalKey(discipline.id, serviceName));
    if (serviceName !== undefined && disciplineName !== '' && service === undefined) {
      const nomeDaDisciplina = discipline?.name ?? disciplineName;
      const key = `${disciplineKey}|${normalizeName(serviceName)}`;
      if (!missingServices.has(key)) {
        missingServices.set(key, { disciplineName: nomeDaDisciplina, serviceName });
      }
      if (!options.createMissingServices) {
        issues.push({
          code: SCHEDULE_ITEM_ERROR.SERVICE_MISSING,
          message: `O serviço "${serviceName}" não existe em ${nomeDaDisciplina}. Marque para criá-lo no catálogo da obra.`,
        });
      } else {
        creates.service = true;
      }
    }

    // ── Datas e números ──────────────────────────────────────────────────────
    const valoresRuins = conferirValores(values);
    if (valoresRuins !== undefined) {
      issues.push({ code: valoresRuins.code, message: valoresRuins.message });
    }

    // ── A mesma chave duas vezes no arquivo (RN-320) ─────────────────────────
    // Por nome, e não por id: o pavimento que ainda vai nascer não tem id, e o nome
    // normalizado é exatamente a chave pela qual ele seria achado.
    const chaveDoArquivo = [
      towerKey,
      normalizeName(floorName),
      disciplineKey,
      normalizeName(serviceName ?? ''),
      normalizeName(segment ?? ''),
    ].join('|');
    if (seen.has(chaveDoArquivo)) {
      issues.push({
        code: SCHEDULE_ITEM_ERROR.DUPLICATE_IN_FILE,
        message: 'A mesma frente, serviço e trecho já apareceram numa linha anterior do arquivo.',
      });
    } else {
      seen.add(chaveDoArquivo);
    }

    const resolvido = {
      ...(tower === undefined ? {} : { towerId: tower.id }),
      ...(floor === undefined ? {} : { floorId: floor.id }),
      ...(discipline === undefined ? {} : { disciplineId: discipline.id }),
      ...(activity === undefined ? {} : { activityId: activity.id }),
      ...(service === undefined ? {} : { serviceId: service.id }),
    };

    if (issues.length > 0) {
      return {
        rowNumber: row.rowNumber,
        status: 'ERROR',
        issues,
        creates: NADA_CRIADO,
        row,
        values,
        ...resolvido,
      };
    }

    // ── Criar ou atualizar ───────────────────────────────────────────────────
    const existing =
      activity === undefined || (serviceName !== undefined && service === undefined)
        ? undefined
        : itemByKey.get(scheduleItemNaturalKey(activity.id, service?.id, segment));

    let status: ScheduleItemRowStatus = 'CREATE';
    if (existing !== undefined) {
      const reimportado = reimportScheduleItem(existing, values, existing.updatedAt);
      // `conferirValores` já passou acima; a recusa aqui seria a mesma.
      status = reimportado.ok && !reimportado.value.changed ? 'UNCHANGED' : 'UPDATE';
    }

    return {
      rowNumber: row.rowNumber,
      status,
      issues,
      creates,
      row,
      values,
      ...resolvido,
      ...(existing === undefined ? {} : { existing }),
    };
  });

  const contar = (status: ScheduleItemRowStatus) =>
    rows.filter((row) => row.status === status).length;

  return {
    summary: {
      total: rows.length,
      create: contar('CREATE'),
      update: contar('UPDATE'),
      unchanged: contar('UNCHANGED'),
      error: contar('ERROR'),
    },
    rows,
    missing: {
      floors: [...missingFloors.values()],
      disciplines: [...missingDisciplines.values()],
      services: [...missingServices.values()],
      activities: missingActivities.size,
    },
  };
}
